import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Calendar } from 'lucide-react';
import { Language, View } from '../../types';
import { swissReveal } from '../../lib/animations';
import { goldenSpring } from '../../lib/fluid-animations';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import ShaderBackground from '../ui/ShaderBackground';
import KineticHeadline from '../ui/KineticHeadline';

// Geometric shape component for hero background
const GeometricShape: React.FC<{
  shape: 'circle' | 'triangle' | 'hexagon' | 'ring' | 'arc';
  className?: string;
  color?: string;
  strokeWidth?: number;
}> = ({ shape, className = '', color = '#0d9488', strokeWidth = 1.5 }) => {
  const renderShape = () => {
    switch (shape) {
      case 'circle':
        return <circle cx="100" cy="100" r="90" fill="none" stroke={color} strokeWidth={strokeWidth} />;
      case 'triangle':
        return <polygon points="100,10 190,170 10,170" fill="none" stroke={color} strokeWidth={strokeWidth} />;
      case 'hexagon':
        return <polygon points="100,10 175,52 175,148 100,190 25,148 25,52" fill="none" stroke={color} strokeWidth={strokeWidth} />;
      case 'ring':
        return (
          <>
            <circle cx="100" cy="100" r="90" fill="none" stroke={color} strokeWidth={strokeWidth} />
            <circle cx="100" cy="100" r="60" fill="none" stroke={color} strokeWidth={strokeWidth} opacity={0.5} />
          </>
        );
      case 'arc':
        return <path d="M 20 150 Q 100 -20 180 150" fill="none" stroke={color} strokeWidth={strokeWidth + 0.5} strokeLinecap="round" />;
      default:
        return <circle cx="100" cy="100" r="90" fill="none" stroke={color} strokeWidth={strokeWidth} />;
    }
  };

  return (
    <svg viewBox="0 0 200 200" className={className}>
      {renderShape()}
    </svg>
  );
};

interface HeroSectionProps {
  lang: Language;
  setView: (v: View) => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({ lang, setView }) => {
  const prefersReducedMotion = useReducedMotion();
  const { scrollY } = useScroll();

  // Enhanced parallax effect - 10% scroll speed (slower background)
  const backgroundY = useTransform(scrollY, [0, 1000], [0, 100]);
  const backgroundScale = useTransform(scrollY, [0, 1000], [1, 1.05]);

  // Parallax transforms for geometric shapes (different speeds for depth)
  const shape1Y = useTransform(scrollY, [0, 1000], [0, -80]);
  const shape2Y = useTransform(scrollY, [0, 1000], [0, -120]);
  const shape3Y = useTransform(scrollY, [0, 1000], [0, -60]);
  const shape4Y = useTransform(scrollY, [0, 1000], [0, -100]);
  const shape1Rotate = useTransform(scrollY, [0, 1000], [0, 15]);
  const shape2Rotate = useTransform(scrollY, [0, 1000], [0, -10]);

  const content = {
    headline: {
      en: "Design a Life You Don't Need to Escape From",
      fa: "زندگی‌ای بساز که نیاز به فرار ازش نداشته باشی"
    },
    subheadline: {
      en: "Through self-awareness and compassion, transform inner conflict into clarity and courageous action.",
      fa: "با خودآگاهی و شفقت، تضادهای درونی رو به وضوح و اقدام شجاعانه تبدیل کن."
    },
    primaryCta: {
      en: "Book Your Free Discovery Call",
      fa: "رزرو جلسه رایگان"
    },
    secondaryCta: {
      en: "Take the Inner Journey",
      fa: "سفر درونی رو شروع کن"
    }
  };

  return (
    <section className="relative min-h-dvh flex items-center justify-center overflow-hidden">
      {/* Shader Background with parallax */}
      <motion.div
        className="absolute inset-0"
        style={prefersReducedMotion ? {} : {
          y: backgroundY,
          scale: backgroundScale,
        }}
      >
        <ShaderBackground interactive={!prefersReducedMotion} />
      </motion.div>

      {/* Parallax Geometric Shapes - Swiss minimalist aesthetic */}
      {!prefersReducedMotion && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Large circle - top right */}
          <motion.div
            className="absolute -top-20 -right-20 md:top-10 md:right-10 w-[300px] h-[300px] md:w-[400px] md:h-[400px] opacity-[0.06]"
            style={{ y: shape1Y, rotate: shape1Rotate }}
            animate={{
              y: [0, -15, 0],
              rotate: [0, 3, 0],
            }}
            transition={{
              duration: 12,
              ease: "easeInOut",
              repeat: Infinity,
              repeatType: "reverse",
            }}
          >
            <GeometricShape shape="circle" color="#0d9488" />
          </motion.div>

          {/* Hexagon - bottom left */}
          <motion.div
            className="absolute bottom-20 -left-16 md:bottom-32 md:left-10 w-[250px] h-[250px] md:w-[350px] md:h-[350px] opacity-[0.05]"
            style={{ y: shape2Y, rotate: shape2Rotate }}
            animate={{
              y: [0, 10, 0],
              rotate: [0, -5, 0],
              scale: [1, 1.02, 1],
            }}
            transition={{
              duration: 15,
              ease: "easeInOut",
              repeat: Infinity,
              repeatType: "reverse",
            }}
          >
            <GeometricShape shape="hexagon" color="#10b981" />
          </motion.div>

          {/* Triangle - center left, smaller */}
          <motion.div
            className="absolute top-1/3 left-[5%] md:left-[10%] w-[120px] h-[120px] md:w-[180px] md:h-[180px] opacity-[0.04]"
            style={{ y: shape3Y }}
            animate={{
              y: [0, -20, 0],
              rotate: [0, 8, 0],
            }}
            transition={{
              duration: 10,
              ease: "easeInOut",
              repeat: Infinity,
              repeatType: "reverse",
            }}
          >
            <GeometricShape shape="triangle" color="#0891b2" />
          </motion.div>

          {/* Arc - top center, subtle */}
          <motion.div
            className="absolute top-[15%] left-1/2 -translate-x-1/2 w-[200px] h-[200px] md:w-[300px] md:h-[300px] opacity-[0.04]"
            style={{ y: shape4Y }}
            animate={{
              y: [0, 12, 0],
              scale: [1, 1.03, 1],
            }}
            transition={{
              duration: 14,
              ease: "easeInOut",
              repeat: Infinity,
              repeatType: "reverse",
            }}
          >
            <GeometricShape shape="arc" color="#0d9488" strokeWidth={2} />
          </motion.div>

          {/* Ring - bottom right, small accent */}
          <motion.div
            className="absolute bottom-[20%] right-[8%] md:right-[15%] w-[100px] h-[100px] md:w-[150px] md:h-[150px] opacity-[0.05]"
            animate={{
              y: [0, -8, 0],
              rotate: [0, 180, 360],
            }}
            transition={{
              duration: 20,
              ease: "linear",
              repeat: Infinity,
            }}
          >
            <GeometricShape shape="ring" color="#14b8a6" strokeWidth={1} />
          </motion.div>
        </div>
      )}

      {/* Gradient overlay: transparent at top, fading to white at bottom */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white" />

      {/* Additional subtle white overlay for text readability */}
      <div className="absolute inset-0 bg-white/30" />

      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
        {/* Label */}
        <motion.span
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, ...swissReveal }}
          className="swiss-label mb-6 block"
        >
          {lang === 'en' ? 'LIFE COACHING' : 'کوچینگ زندگی'}
        </motion.span>

        {/* Headline with Kinetic Typography */}
        <KineticHeadline
          as="h1"
          className="type-display font-bold text-[2.75rem] md:text-[4rem] lg:text-[5rem] xl:text-[5.5rem] text-center text-brandDark mb-8 md:mb-12"
          delay={0.2}
        >
          {lang === 'en' ? (
            <>
              Design a life<br/>
              <span className="type-display-italic text-primary">full of good mistakes!</span>
            </>
          ) : (
            <>
              طراحی زندگی<br/>
              <span className="type-display-italic text-primary">پر از اشتباهات خوب</span>
            </>
          )}
        </KineticHeadline>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, ...goldenSpring }}
          className="swiss-body text-lg md:text-xl text-gray-500 max-w-2xl mx-auto mb-12 text-pretty"
        >
          {content.subheadline[lang]}
        </motion.p>

        {/* CTAs - Stacked layout with primary button and elegant text link */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, ...goldenSpring }}
          className="flex flex-col items-center gap-6"
        >
          {/* Primary CTA - Take the Journey */}
          <button
            onClick={() => setView('journey')}
            className="btn btn-primary btn-lg focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            <ArrowRight className="w-5 h-5" aria-hidden="true" />
            {content.secondaryCta[lang]}
          </button>

          {/* Secondary CTA - Book a call, scrolls to contact */}
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              const contactSection = document.getElementById('contact');
              if (contactSection) {
                contactSection.scrollIntoView({ behavior: 'smooth' });
              } else {
                window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
              }
            }}
            className="group relative inline-flex items-center gap-2 text-brandDark/70 hover:text-brandDark transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded"
          >
            <Calendar className="w-4 h-4" aria-hidden="true" />
            <span className="relative">
              {content.primaryCta[lang]}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-primary group-hover:w-full transition-all duration-300 motion-reduce:transition-none" />
            </span>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
